<template>
  <div v-if="positionData && form" class="no-overflow-modal" @input.capture="markAssistantEdit" @change.capture="markAssistantEdit">
    <div class="columns">
      <div class="column b-r has-text-left" dir="ltr">
        <div>
          <div ref="assistant-area-1" class="box is-flex is-justify-content-space-between">
            <div class="has-text-left is-flex-grow-1">
              <p class="columns">
                <b class="column is-5">Company: </b>
                <span class="column">{{positionData.company.name}} <TopFirmBadge :company="positionData.company" /></span>
              </p>
              <p class="columns">
                <b class="column is-5">Position ID: </b>
                <span class="column">{{positionData.id}}</span>
              </p>
              <p class="columns">
                <b class="column is-5">Company Position ID: </b>
                <input ref="assistant-company_position_id" class="input" type="text" placeholder="..." v-model="form.data.position.fields.company_position_id.value" @focusout="handleCompanyPositionId" />
              </p>
              <p class="columns">
                <b class="column is-5">Position Applicant Code: </b>
                <span class="column">{{$utils.getPositionApplicantCode(positionData.id)}}</span>
              </p>
              <p class="columns is-align-items-center">
                <b class="column is-5">Data Updated At: </b>
                <span v-if="dateUpdateAtView" class="column is-3">{{$moment(dateUpdateAtView).format('DD/MM/YYYY')}}</span>
                <button @click="setActiveAt" class="column is-2 button p-0 is-small is-info">Set</button>
              </p>
              <p class="columns pl-3">
                <LocationEditor :onSubmit="handleLocation" :payload="{ location: location, resource_type: 'Position', resource_id: positionData.id }" />
              </p>
              <p>
              <PriorityWarning ref="assistant-priority_warning" :positionId="positionData.id" v-model="form.data.position.fields.priority_warning.value" />
              </p>
            </div>
            <div>
              <GhanaFlag :payload="positionData" :additional_data="{ resource_type: 'Position' }" :eager_load="true" />
            </div>
          </div>
          <div ref="assistant-area-2" class="box mt-4">
            <div class="is-flex mb-1">
              <div class="is-flex-grow-1 mr-4">
                <MinSeniority ref="assistant-min_seniority" :positionId="positionData.id" placeholder="Min Seniority" v-model="form.data.position.fields.min_seniority.value" />
              </div>
              <div class="is-flex-grow-1">
                <MaxSeniority ref="assistant-max_seniority"
                  :positionId="positionData.id"
                  placeholder="Max Seniority"
                  v-model="form.data.position.fields.max_seniority.value"
                  @save-start="onMaxSenioritySaveStart"
                  @saved="onMaxSenioritySaved"
                  @save-failed="onMaxSenioritySaveFailed"
                />
                <MaxSeniorityCheck
                  :check="maxCheck"
                  :savedMax="maxSavedValue"
                  :inputValue="form.data.position.fields.max_seniority.value"
                  :busy="maxCheckBusy || maxSaving"
                  :message="maxCheckMessage"
                  @confirm="confirmMaxSeniority"
                />
              </div>
            </div>
            <p v-if="seniorityWillSwap" class="is-size-7 has-text-warning-dark mb-1" dir="rtl" data-test="seniority-swap-hint">{{ rangeSwapHint }}</p>
            <p class="is-size-7 has-text-grey mb-4 seniority-hint" dir="rtl">
              יש להזין טווח ותק רחב לפי שיקול דעת, ולא בהכרח את הטווח שהלקוח כתב. המערכת מתייחסת למספר כפי שהוזן.
            </p>
            <JobTitles @updateJobTitles="assistantJobTitles = $event" ref="assistant-job_title_ids" :position="positionData" />
          </div>
          <div class="box mt-5">
            <div class="field">
              <MaxSalaryExpectations :positionId="positionData.id" v-model="form.data.position.fields.max_salary_expectations.value" />
            </div>
            <div ref="assistant-area-3" class="assistant-area">
            <div class="field mt-4">
              <SalaryExpectationsComment ref="assistant-salary_expectations_comment" :positionId="positionData.id" v-model="form.data.position.fields.salary_expectations_comment.value" />
            </div>
            <div class="box mt-4">
              <div class="mb-3">
                <label class="is-size-5">Skills</label>
              </div>
              <p v-if="hasSkillVerifications" class="is-size-7 has-text-grey mb-2" dir="rtl">
                פירוט מהשיחה עם {{skillVerificationsFor}} מוצג ליד כל כישור
              </p>
              <DisplaySkills :key="assistantSkillsRevision" ref="assistant-skill_ids" v-if="positionData.positions_skills" :payload="positionData" :verifications="skillVerifications" @verifications-matched="skillVerificationsMatched = $event" />
              <AiDisplaySkills v-if="positionData.id && positionData.company" :position_id="positionData.id" :current_company_id="positionData.company.id" />
            </div>
            <div class="field mt-2">
              <IsDiscrete ref="assistant-is_discrete" :positionId="positionData.id" v-model="form.data.position.fields.is_discrete.value" />
            </div>
            <div class="field mt-4">
              <WorkEntails ref="assistant-work_entails" :positionId="positionData.id" v-model="workEntails" />
            </div>
            </div>
            <div class="field mt-4">
              <Abstract ref="assistant-abstract" :positionId="positionData.id" v-model="form.data.position.fields.abstract.value" />
            </div>
            <div v-if="positionData.created_by" class="has-text-left mt-4">
              <p>
                <b>נפתחה ע"י:</b>
                <span>{{ createdByLabel }}</span>
              </p>
            </div>
            <div class="has-text-left mt-4">
              <p><b>Internal Notes:</b></p>
              <InternalNotes :payload="positionData" :additional_data="{ resource_type: 'Position' }" :is_modal="false" :paged="true" />
            </div>
            <div class="mt-5">
              <Owner ref="assistant-owner" :positionId="positionData.id" v-model="form.data.position.fields.owner.value" />
            </div>
            <div ref="assistant-area-4" class="assistant-area">
            <div class="field mt-2">
              <Message ref="assistant-message" :positionId="positionData.id" v-model="form.data.position.fields.message.value" />
            </div>
            <div class="box is-flex is-align-items-center" data-test="position-status-box">
              <IsHot :payload="positionData" class="mr-5" />
              <div class="ml-5 is-flex-grow-1">
                <Status :positionId="positionData.id" v-model="form.data.position.fields.status.value" />
              </div>
            </div>
            <div class="mt-3">
              <Priority :positionId="positionData.id" v-model="form.data.position.fields.priority.value" />
            </div>
            <div class="field mt-4">
              <ContactEmail ref="assistant-contact_email" :positionId="positionData.id" v-model="contactEmail" />
            </div>
            <div class="field mt-4">
              <ContactPhones ref="assistant-contact_phones" :positionId="positionData.id" v-model="contactPhones" />
            </div>
            </div>
          </div>
          <div class="box is-flex is-justify-content-space-between is-align-items-center">
            <button @click="generateOffers" class="button is-info">Generate Offers</button>
            <router-link :to="{ name: 'company-positions', query: { company_id: positionData.company.resource_id, show_id: positionData.id } }" class="blue_text">
              <b><u>GO TO POSITION</u></b>
            </router-link>
            <button @click="outsource" class="button is-info">Outsource</button>
            <button @click="createPost" class="button is-info">Create Post</button>
            <button @click="RelatedPositions" class="button is-info">Related Positions</button>
            <button @click="aiWorkPosition" class="button is-info">AI Work</button>
          </div>
        </div>
      </div>
      <div class="column b-r" style="overflow-y: auto; overflow-x: hidden;">
        <div class="box">
          <!-- Screening questions asked by the chatbot: first in this column so it is
               visible without scrolling (the raw text below is 20 rows tall) -->
          <PositionScreeningQuestions
            ref="screeningEditor"
            class="mb-4"
            :class="{ 'screening-focus': highlightScreening }"
            :positionId="positionData.id"
            :questions="screeningQuestionsList"
            :revision="positionData.screening_questions_revision || 0"
          />

          <div class="field mb-3">
            <!-- who last changed the raw text, and when — same muted provenance style as the applicant Abstract -->
            <div class="is-flex is-align-items-baseline mb-2">
              <label class="label m-0">Company Raw Text</label>
              <span v-if="rawTextUpdatedLabel" class="is-size-7 has-text-grey-light ml-2 raw-text-updated" dir="rtl">{{ rawTextUpdatedLabel }}</span>
            </div>
            <div class="control">
              <textarea
                class="textarea is-fullwidth"
                placeholder="..."
                rows="20"
                v-model="form.data.position.fields.company_raw_text.value"
                @focusout="handleFocusOut"
              />
            </div>
            <PositionCreationAssistant v-if="assistantSession && assistantReady && assistantJobTitles !== null" class="mt-2"
              :session="assistantSession" :draft="assistantDraft" :companyId="companyId"
              :rawText="form.data.position.fields.company_raw_text.value || ''" :apply="applyAssistantSuggestion"
              :disabled="!!assistantLoadError" @working-fields="assistantWorkingFields = $event" @working-area="assistantWorkingArea = $event" />
            <p v-if="assistantLoadError" role="alert">{{ assistantLoadError }}</p>
          </div>

          <!-- WhatsApp Templates Section -->
          <WhatsappTemplates
            v-model="whatsappTemplates"
            @input="handleWhatsappTemplatesChange"
          />
        </div>

        <!-- Read-only "מדיניות שליחה" notes for this position (+ company rules) -->
        <div v-if="positionData.company" class="box" dir="rtl">
          <SendingPolicyNotes
            :companyId="companyId"
            :positionId="positionData.id"
            :startOpen="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapMutations } from 'vuex'
import PositionCreationAssistant from '@/components/PositionCreationAssistant.vue'
import positionAssistantMixin from '@/mixins/positionAssistantMixin'

import GhanaFlag from '@/components/GhanaFlag.vue'
import LocationEditor from '@/components/LocationEditor'
import InternalNotes from '@/components/InternalNotes.vue'
import DisplaySkills from '@/components/DisplaySkills.vue'
import AiDisplaySkills from '@/components/AiDisplaySkills.vue'
import JobTitles from '@/components/entities/position/JobTitles'
import PriorityWarning from '@/components/entities/position/PriorityWarning'
import IsDiscrete from '@/components/entities/position/IsDiscrete'
import SalaryExpectationsComment from '@/components/entities/position/SalaryExpectationsComment'
import MaxSalaryExpectations from '@/components/entities/position/MaxSalaryExpectations'
import Abstract from '@/components/entities/position/Abstract'
import Message from '@/components/entities/position/Message'
import ContactEmail from '@/components/entities/position/ContactEmail'
import ContactPhones from '@/components/entities/position/ContactPhones'
import Owner from '@/components/entities/position/Owner'
import MinSeniority from '@/components/entities/position/MinSeniority'
import MaxSeniority from '@/components/entities/position/MaxSeniority'
import MaxSeniorityCheck from '@/components/entities/position/MaxSeniorityCheck'
import Status from '@/components/entities/position/Status'
import Priority from '@/components/entities/position/Priority'
import WorkEntails from '@/components/entities/position/WorkEntails'
import WhatsappTemplates from '@/components/entities/position/WhatsappTemplates'
import PositionScreeningQuestions from '@/components/entities/position/PositionScreeningQuestions'
import IsHot from '@/components/IsHot'
import SendingPolicyNotes from '@/components/SendingPolicyNotes.vue'
import TopFirmBadge from '@/components/entities/company/TopFirmBadge.vue'
import rawTextAuditMixin from '@/mixins/rawTextAuditMixin.js'
import maxSeniorityCheckMixin from '@/mixins/maxSeniorityCheckMixin.js'
import { rangeWillSwap, RANGE_SWAP_HINT } from '@/custom/helpers/range_swap'

export default {
  name: 'ModalPosition',
  mixins: [rawTextAuditMixin, maxSeniorityCheckMixin, positionAssistantMixin],
  components: {
    PositionCreationAssistant,
    TopFirmBadge,
    GhanaFlag,
    LocationEditor,
    InternalNotes,
    DisplaySkills,
    AiDisplaySkills,
    JobTitles,
    PriorityWarning,
    IsDiscrete,
    SalaryExpectationsComment,
    MaxSalaryExpectations,
    Abstract,
    MinSeniority,
    MaxSeniority,
    MaxSeniorityCheck,
    Message,
    ContactEmail,
    ContactPhones,
    Owner,
    Status,
    Priority,
    WorkEntails,
    WhatsappTemplates,
    PositionScreeningQuestions,
    IsHot,
    SendingPolicyNotes
  },
  props: {
    updateTitle: Function,
    payload: {
      type: Object
    }
  },
  data () {
    return {
      positionData: undefined,
      form: undefined,
      abstract: undefined,
      location: undefined,
      workEntails: '',
      contactEmail: '',
      contactPhones: '',
      whatsappTemplates: {
        interest: '',
        infoRequest: '',
        conversationEnd: ''
      },
      saveTimeout: null,
      isSaving: false,
      isUpdatingFromServer: false,
      // פירוט הכישורים מהשיחה עם המועמד. null = אין הקשר מועמד / הטעינה נכשלה,
      // ואז מסך הכישורים מוצג בדיוק כמו קודם.
      skillVerifications: null,
      // כמה מכישורי המשרה קיבלו פירוט בפועל (מדווח ע"י DisplaySkills).
      skillVerificationsMatched: 0,
      highlightScreening: false
    }
  },
  computed: {
    screeningQuestionsList () {
      let q = this.positionData && this.positionData.screening_questions
      if (typeof q === 'string') {
        try { q = JSON.parse(q) } catch (e) { q = [] }
      }
      return Array.isArray(q) ? q : []
    },
    rangeSwapHint () {
      return RANGE_SWAP_HINT
    },
    seniorityWillSwap () {
      const f = this.form && this.form.data.position.fields
      return !!f && rangeWillSwap(f.min_seniority.value, f.max_seniority.value)
    },
    companyId () {
      return this.positionData.company.resource_id
    },
    hasSkillVerifications () {
      return this.skillVerificationsMatched > 0
    },
    skillVerificationsFor () {
      const name = this.payload && this.payload.data ? this.payload.data.applicant_name : ''
      return name || 'המועמד'
    },
    dateUpdateAtView () {
      return this.form.data.position.fields.active_at.value
    },
    createdByLabel () {
      const map = { agent: 'סוכן מייל (אוטומטי)', chatbot: "צ'אט בוט מעסיקים (אוטומטי)" }
      return map[this.positionData.created_by] || this.positionData.created_by
    }
  },
  async created () {
    // לא ב-await: פירוט הכישורים הוא תוספת, ואסור לו לעכב את פתיחת המודל.
    this.loadSkillVerifications()
    this.positionData = await this.$network_utils.getPosition({ id: this.payload.data.id })
    this.setForm(this.positionData)
    this.initRawTextAudit(this.positionData)
    this.initMaxSeniorityCheck(this.positionData)
    try {
      await Promise.all([this.getPositionLocation(), this.getTexts()])
      this.assistantReady = true
    } catch (error) { this.assistantLoadError = 'טעינת פרטי המשרה נכשלה. יש לפתוח מחדש את החלון.' }
    this.initializeWhatsappTemplates()
    this.focusScreeningIfRequested()
  },
  beforeDestroy () {
    // Clear any pending save operations
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
  },
  methods: {
    ...mapMutations('modals', ['create', 'remove']),
    // payload.focus === 'screening' (opened from the positions table icon):
    // scroll the screening editor into view and highlight it briefly.
    focusScreeningIfRequested () {
      if (!this.payload || this.payload.focus !== 'screening') return
      this.$nextTick(() => {
        const ref = this.$refs.screeningEditor
        const el = ref && ref.$el
        if (!el) return
        if (el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        this.highlightScreening = true
        setTimeout(() => { this.highlightScreening = false }, 2500)
      })
    },
    // מה שהמועמד סיפר על כל כישור בשיחה עם הצ'אטבוט. רץ רק כשהמודל נפתח מתוך
    // הקשר של מועמד (מסך הצעות / משרה מיועדת). כישלון נבלע בכוונה — המודל
    // נפתח כרגיל גם כש-conv_svc לא זמין, בלי הודעת שגיאה על משרה תקינה.
    async loadSkillVerifications () {
      const applicant_id = this.payload && this.payload.data ? this.payload.data.applicant_id : null
      if (!applicant_id) return
      try {
        const response = await this.$network_utils.getConvSvcApplicantSkillVerifications({ applicant_id })
        this.skillVerifications = response && Array.isArray(response.skills) ? response.skills : null
      } catch (error) {
        this.skillVerifications = null
      }
    },
    setForm (form_data) {
      this.form = new this.$form({
        position: {
          fields: {
            title: {
              value: form_data.title,
              has_error: false,
              validations: []
            },
            abstract: {
              value: form_data.abstract || '',
              has_error: false,
              validations: []
            },
            status: {
              value: form_data.status,
              has_error: false,
              validations: []
            },
            owner: {
              value: form_data.owner || this.$utils.getOwner().name,
              has_error: false,
              validations: []
            },
            company_position_id: {
              value: form_data.company_position_id || '',
              has_error: false,
              validations: []
            },
            priority_warning: {
              value: form_data.priority_warning || '',
              has_error: false,
              validations: []
            },
            priority: {
              value: form_data.priority,
              has_error: false,
              validations: []
            },
            min_seniority: {
              value: form_data.min_seniority || '',
              has_error: false,
              validations: []
            },
            max_seniority: {
              value: form_data.max_seniority || '',
              has_error: false,
              validations: []
            },
            message: {
              value: form_data.message || '',
              has_error: false,
              validations: []
            },
            max_salary_expectations: {
              value: parseInt(form_data.max_salary_expectations),
              has_error: false,
              validations: ['Min', 'Max', 'Number'],
              min: 5000,
              max: 100000
            },
            salary_expectations_comment: {
              value: form_data.salary_expectations_comment || '',
              has_error: false,
              validations: []
            },
            company_raw_text: {
              value: form_data.company_raw_text,
              has_error: false,
              validations: []
            },
            is_discrete: {
              value: String(form_data.is_discrete),
              has_error: false,
              validations: []
            },
            active_at: {
              value: form_data.active_at,
              has_error: false,
              validations: []
            }
          },
          request_details: {
            method: 'put',
            url: `/positions/${this.positionData.id}`,
            headers: 'admin_headers'
          },
          is_used: true
        }
      })
    },
    handleLocation () {
      return this.getPositionLocation()
    },
    async generateOffers () {
      try {
        await this.$network_utils.sendPositionUpdateEvent({
          company_id: this.companyId,
          position_id: this.positionData.id,
          status: this.positionData.status
        })
        this.$utils.emit('show_toast', { text: 'נשלח בהצלחה' })
      } catch (e) {
        this.$utils.emit('show_toast', { text: 'failed' })
      }
    },
    async setActiveAt () {
      const date = new Date()
      this.form.data.position.fields.active_at.value = date
      await this.$network_utils.updatePosition({ position_id: this.positionData.id, active_at: date })
    },
    outsource () {
      this.create({
        designator: 'edit-outsource-position-form-modal',
        component_name: 'OutsourcePositionForm',
        env_position_id: this.positionData.id,
        modal_title: 'Outsource - ' + this.positionData.title
      })
    },
    createPost () {
      this.create({
        designator: 'create-position-post-form',
        component_name: 'CreatePositionPostForm',
        position_id: this.positionData.id,
        modal_title: 'Create Position Post - ' + this.positionData.title,
        is_center_modal: true,
        modal_width: 1700
      })
    },
    RelatedPositions () {
      this.create({
        designator: 'connected-position-manager-modal',
        component_name: 'ConnectedPositionManager',
        position: this.positionData,
        modal_title: this.positionData.title + ' - Connected Position Manager',
        is_center_modal: true,
        modal_width: 1400
      })
    },
    aiWorkPosition () {
      if (!confirm('תן ל-AI לעבוד על המשרה "' + this.positionData.title + '"?')) return
      this.$network_utils
        .aiWorkPosition({ position_id: this.positionData.id })
        .then(() => alert('הסוכן הופעל על המשרה'))
        .catch(err => alert('שגיאה בהפעלת הסוכן: ' + (err && err.message ? err.message : err)))
    },
    async handleCompanyPositionId () {
      const companyPositionId = this.form.data.position.fields.company_position_id.value
      await this.$network_utils.updatePosition({ position_id: this.positionData.id, company_position_id: companyPositionId })
    },
    async handleFocusOut () {
      const companyRawText = this.form.data.position.fields.company_raw_text.value
      const token = this.beginRawTextSave(companyRawText)
      const response = await this.$network_utils.updatePosition({ position_id: this.positionData.id, company_raw_text: companyRawText })
      this.finishRawTextSave(token, response)
    },
    async getPositionLocation () {
      const [ location ] = await this.$network_utils.getResourceLocations({ current_type: 'Position', current_resource_id: this.positionData.id })
      this.location = location
    },
    async getTexts () {
      const texts = await this.$network_utils.getResourceTexts({ current_type: 'Position', current_resource_id: this.positionData.id })
      this.workEntails = (texts.find(text => text.designator == 'work_entails') || {}).content || ''
      this.contactEmail = (texts.find(text => text.designator == 'contact_email') || {}).content || ''
      this.contactPhones = this.positionData.contact_phones || ''
    },
    handleWhatsappTemplatesChange (templates) {
      // Prevent recursive updates
      if (this.isUpdatingFromServer || this.isSaving) {
        return
      }

      this.whatsappTemplates = templates

      // Clear existing timeout
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout)
      }

      // Debounce the save operation - only save after user stops typing for 1 second
      this.saveTimeout = setTimeout(() => {
        this.saveWhatsappTemplates()
      }, 1000)
    },
    async saveWhatsappTemplates () {
      // Prevent multiple simultaneous saves
      if (this.isSaving) {
        return
      }

      this.isSaving = true

      try {
        await this.$network_utils.updatePosition({
          position_id: this.positionData.id,
          whatsapp_template_interest: this.whatsappTemplates.interest,
          whatsapp_template_info_request: this.whatsappTemplates.infoRequest,
          whatsapp_template_conversation_end: this.whatsappTemplates.conversationEnd
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving WhatsApp templates:', error)
        this.$utils.emit('show_toast', { text: 'Error saving WhatsApp templates' })
      } finally {
        this.isSaving = false
      }
    },
    initializeWhatsappTemplates () {
      this.isUpdatingFromServer = true

      this.whatsappTemplates = {
        interest: this.positionData.whatsapp_template_interest || '',
        infoRequest: this.positionData.whatsapp_template_info_request || '',
        conversationEnd: this.positionData.whatsapp_template_conversation_end || ''
      }

      // Use nextTick to ensure the update is complete before allowing new changes
      this.$nextTick(() => {
        this.isUpdatingFromServer = false
      })
    }
  }
}
</script>
<style scoped>
  .b-r {
    height: 65vh;
    overflow-y: auto;
  }
  .screening-focus {
    outline: 3px solid #f59e0b;
    outline-offset: 4px;
    border-radius: 6px;
    transition: outline-color 0.5s;
  }
</style>
